import { ChartCandle, ChartTick, ChartConfig } from "../../types/chart";

export class ChartService {
  private currentConfig: ChartConfig | null = null;
  private sseConnection: EventSource | null = null;
  private currentInstrumentId: string | null = null;
  private currentReader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private subscriptions: Map<string, Function[]> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;

  constructor() {
    this.subscriptions.set("ohlc", []);
    this.subscriptions.set("ticks", []);
    this.subscriptions.set("prices", []);
  }

  // Historical data methods - real API only (no fallback)
  async getHistoricalCandles(
    instrumentId: string,
    interval: string,
    from?: string,
    to?: string,
    limit: number = 500
  ): Promise<ChartCandle[]> {
    // Validate supported intervals (per API schema)
    const supportedIntervals = ["1m", "5m", "15m", "1h", "1d"];
    if (!supportedIntervals.includes(interval)) {
      throw new Error(
        `Interval "${interval}" is not supported. Supported intervals: ${supportedIntervals.join(", ")}`
      );
    }

    try {
      const realData = await this.fetchRealHistoricalCandles(
        instrumentId,
        interval,
        from,
        to,
        limit
      );
      if (realData && realData.length > 0) {
        return realData;
      } else {
        throw new Error("No chart data available for this instrument");
      }
    } catch (error) {
      console.error("Failed to fetch chart data:", error);
      throw new Error(
        `Chart data unavailable: ${error instanceof Error ? error.message : "Something went wrong"}`
      );
    }
  }

  async getHistoricalTicks(
    instrumentId: string,
    from?: string,
    to?: string,
    limit: number = 1000
  ): Promise<ChartTick[]> {
    try {
      const realData = await this.fetchRealHistoricalTicks(
        instrumentId,
        from,
        to,
        limit
      );
      if (realData && realData.length > 0) {
        return realData;
      } else {
        throw new Error("No tick data available for this instrument");
      }
    } catch (error) {
      console.error("Failed to fetch tick data:", error);
      throw new Error(
        `Tick data unavailable: ${error instanceof Error ? error.message : "Something went wrong"}`
      );
    }
  }

  // Real API integration methods
  private async fetchRealHistoricalCandles(
    instrumentId: string,
    interval: string,
    from?: string,
    to?: string,
    limit: number = 500
  ): Promise<ChartCandle[]> {
    // Import API client dynamically to avoid circular dependencies
    const { apiClient } = await import("./client");

    const params = new URLSearchParams();
    params.append("interval", interval);
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    params.append("limit", Math.min(limit, 1000).toString());

    const response = (await apiClient.get(
      `/v1/instruments/${instrumentId}/candles?${params}`
    )) as any;

    // Handle direct candles array response
    if (response?.candles && Array.isArray(response.candles)) {
      return response.candles.map((candle: any) => ({
        timestamp: new Date(candle.timestamp).getTime(),
        open: Number(candle.open),
        high: Number(candle.high),
        low: Number(candle.low),
        close: Number(candle.close),
        volume: Number(candle.volume || 0),
      }));
    }

    // Fallback: handle nested data structure
    if (response?.success && response?.data?.candles) {
      return response.data.candles.map((candle: any) => ({
        timestamp: new Date(candle.timestamp).getTime(),
        open: Number(candle.open),
        high: Number(candle.high),
        low: Number(candle.low),
        close: Number(candle.close),
        volume: Number(candle.volume || 0),
      }));
    }

    throw new Error(
      `API request failed: ${response?.error || "No candles data found"}`
    );
  }

  private async fetchRealHistoricalTicks(
    instrumentId: string,
    from?: string,
    to?: string,
    limit: number = 1000
  ): Promise<ChartTick[]> {
    // Import API client dynamically to avoid circular dependencies
    const { apiClient } = await import("./client");

    const params = new URLSearchParams();
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    params.append("limit", Math.min(limit, 5000).toString());

    const response = (await apiClient.get(
      `/v1/instruments/${instrumentId}/ticks?${params}`
    )) as any;

    // Handle direct ticks array response (per API schema)
    if (response?.ticks && Array.isArray(response.ticks)) {
      return response.ticks.map((tick: any) => ({
        timestamp: new Date(tick.timestamp).getTime(),
        bid: Number(tick.bid),
        ask: Number(tick.ask),
      }));
    }

    // Fallback: handle nested data structure
    if (response?.success && response?.data?.ticks) {
      return response.data.ticks.map((tick: any) => ({
        timestamp: new Date(tick.timestamp).getTime(),
        bid: Number(tick.bid),
        ask: Number(tick.ask),
      }));
    }

    throw new Error(
      `API request failed: ${response?.error || "No ticks data found"}`
    );
  }

  // Real-time streaming methods
  subscribeToOHLC(
    instrumentId: string,
    intervals: string[],
    callback: (data: ChartCandle) => void
  ): void {
    // Check if we're switching to a different instrument
    if (this.currentInstrumentId && this.currentInstrumentId !== instrumentId) {
      this.forceUnsubscribe();
    }

    // Store callback for this subscription type
    const callbacks = this.subscriptions.get("ohlc") || [];
    callbacks.push(callback);
    this.subscriptions.set("ohlc", callbacks);

    // Update current instrument
    this.currentInstrumentId = instrumentId;

    // Start SSE connection if not already connected OR if we switched instruments
    if (!this.sseConnection) {
      this.startSSEConnection(instrumentId, intervals);
    }
  }

  subscribeToTicks(
    instrumentId: string,
    callback: (data: ChartTick) => void
  ): void {
    // Check if we're switching to a different instrument
    if (this.currentInstrumentId && this.currentInstrumentId !== instrumentId) {
      this.forceUnsubscribe();
    }

    // Store callback for this subscription type
    const callbacks = this.subscriptions.get("ticks") || [];
    callbacks.push(callback);
    this.subscriptions.set("ticks", callbacks);

    // Update current instrument
    this.currentInstrumentId = instrumentId;

    // Start SSE connection if not already connected OR if we switched instruments
    if (!this.sseConnection) {
      this.startSSEConnection(instrumentId, []);
    }
  }

  subscribeToPrices(
    instrumentId: string,
    callback: (data: { bid: number; ask: number; timestamp: string }) => void
  ): void {
    // Check if we're switching to a different instrument
    if (this.currentInstrumentId && this.currentInstrumentId !== instrumentId) {
      this.forceUnsubscribe();
    }

    const callbacks = this.subscriptions.get("prices") || [];
    callbacks.push(callback);
    this.subscriptions.set("prices", callbacks);

    // Update current instrument
    this.currentInstrumentId = instrumentId;

    if (!this.sseConnection) {
      this.startSSEConnection(instrumentId, []);
    }
  }

  private async startSSEConnection(
    instrumentId: string,
    intervals: string[]
  ): Promise<void> {
    try {
      // Import dependencies
      const { apiClient } = await import("./client");

      const token = apiClient.getToken();
      if (!token) {
        throw new Error("No authentication token available");
      }

      // Build SSE URL with chart channels
      const channels = ["ohlc", "ticks", "prices"].filter(
        (channel) =>
          this.subscriptions.get(channel)?.length &&
          this.subscriptions.get(channel)!.length > 0
      );

      const params = new URLSearchParams();
      params.append("channels", channels.join(","));
      params.append("instruments", instrumentId);
      if (intervals.length > 0) {
        params.append("ohlc_interval", intervals.join(","));
      }

      const sseUrl = `https://aimock.mobile-bot.deriv.dev/v1/stream/connect?${params}`;

      // Use fetch with authorization header instead of EventSource
      const response = await fetch(sseUrl, {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          "Cache-Control": "no-cache",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Chart SSE connection failed: ${response.status} ${response.statusText}`
        );
      }

      if (!response.body) {
        throw new Error("Chart SSE response body is null");
      }

      this.reconnectAttempts = 0;

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      // Store reader reference for cleanup
      this.currentReader = reader;

      this.readChartSSEStream(reader, decoder);
    } catch (error) {
      console.error("Failed to start chart SSE connection:", error);
      this.handleSSEError();
    }
  }

  /**
   * Read chart SSE stream from fetch response
   */
  private async readChartSSEStream(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    decoder: TextDecoder
  ): Promise<void> {
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
          this.parseChartSSEEvent(line.trim());
        }
      }
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }

      console.error("Error reading chart SSE stream:", error);
      this.handleSSEError();
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Parse chart SSE event line
   */
  private parseChartSSEEvent(line: string): void {
    if (line.startsWith("data: ")) {
      try {
        const data = line.substring(6); // Remove 'data: ' prefix
        const eventData = JSON.parse(data);
        this.handleSSEMessage(eventData);
      } catch (error) {
        console.error("Failed to parse chart SSE data:", error);
      }
    } else if (line.startsWith("event: ")) {
      // Handle different event types if needed
      // const eventType = line.substring(7); // Event type not currently used
    }
  }

  private handleSSEMessage(data: any): void {
    // Handle direct OHLC data structure (when data IS the OHLC object)
    if (
      data.event === "ohlc" ||
      (data.timestamp && data.open && data.high && data.low && data.close)
    ) {
      let ohlcData = data;

      // If it's wrapped in an event structure, extract the data
      if (data.data && data.event === "ohlc") {
        ohlcData = data.data;
      }

      const candle: ChartCandle = {
        timestamp: new Date(ohlcData.timestamp).getTime(),
        open: Number(ohlcData.open),
        high: Number(ohlcData.high),
        low: Number(ohlcData.low),
        close: Number(ohlcData.close),
        volume: Number(ohlcData.volume || 0),
      };

      const callbacks = this.subscriptions.get("ohlc") || [];

      callbacks.forEach((callback) => {
        callback(candle);
      });

      return; // Exit early since we handled the OHLC event
    }

    // Handle tick events (per API schema: direct properties, not nested under data)
    if (
      data.event === "tick" ||
      (data.instrumentId && data.bid && data.ask && data.timestamp)
    ) {
      let tickData = data;

      // If it's wrapped in an event structure, extract the data
      if (data.data && data.event === "tick") {
        tickData = data.data;
      }

      const tick: ChartTick = {
        timestamp: new Date(tickData.timestamp).getTime(),
        bid: Number(tickData.bid),
        ask: Number(tickData.ask),
      };

      const callbacks = this.subscriptions.get("ticks") || [];

      callbacks.forEach((callback) => {
        callback(tick);
      });

      return; // Exit early since we handled the tick event
    }

    // Handle price events (per API schema: direct properties, not nested under data)
    if (
      data.event === "price" ||
      (data.instrumentId &&
        data.bid &&
        data.ask &&
        data.timestamp &&
        !data.open)
    ) {
      let priceData = data;

      // If it's wrapped in an event structure, extract the data
      if (data.data && data.event === "price") {
        priceData = data.data;
      }

      const price = {
        bid: Number(priceData.bid),
        ask: Number(priceData.ask),
        timestamp: priceData.timestamp,
      };

      const callbacks = this.subscriptions.get("prices") || [];

      callbacks.forEach((callback) => {
        callback(price);
      });
    }
  }

  private handleSSEError(): void {
    if (this.sseConnection) {
      this.sseConnection.close();
      this.sseConnection = null;
    }

    // Implement exponential backoff for reconnection
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);

      setTimeout(() => {
        this.reconnectAttempts++;
        // Would need to restart connection with previous parameters
      }, delay);
    } else {
      console.error("Max chart SSE reconnection attempts reached");
    }
  }

  // Chart management
  updateConfig(config: ChartConfig): void {
    this.currentConfig = config;
  }

  getCurrentConfig(): ChartConfig | null {
    return this.currentConfig;
  }

  isConnected(): boolean {
    return this.sseConnection?.readyState === EventSource.OPEN;
  }

  unsubscribe(): void {
    // Clear all subscription callbacks
    this.subscriptions.clear();
    this.subscriptions.set("ohlc", []);
    this.subscriptions.set("ticks", []);
    this.subscriptions.set("prices", []);

    // Close SSE connection
    if (this.sseConnection) {
      this.sseConnection.close();
      this.sseConnection = null;
    }

    // Close reader if active
    if (this.currentReader) {
      try {
        this.currentReader.cancel();
      } catch (error) {
        console.warn("Error canceling reader:", error);
      }
      this.currentReader = null;
    }

    this.reconnectAttempts = 0;
    this.currentInstrumentId = null;
  }

  private forceUnsubscribe(): void {
    // Clear all subscription callbacks
    this.subscriptions.clear();
    this.subscriptions.set("ohlc", []);
    this.subscriptions.set("ticks", []);
    this.subscriptions.set("prices", []);

    // Force close SSE connection
    if (this.sseConnection) {
      try {
        this.sseConnection.close();
      } catch (error) {
        console.warn("Error closing SSE connection:", error);
      }
      this.sseConnection = null;
    }

    // Force close reader if active
    if (this.currentReader) {
      try {
        this.currentReader.cancel();
      } catch (error) {
        console.warn("Error canceling reader:", error);
      }
      this.currentReader = null;
    }

    this.reconnectAttempts = 0;
    // Don't reset currentInstrumentId here as it will be updated by the calling method
  }
}

// Create singleton instance
export const chartService = new ChartService();
